import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

import { trackPageView } from './analytics'

/**
 * Send a GA4 `page_view` for each client-side navigation (VIT-17882). A no-op outside
 * production, where `trackPageView` itself is a no-op.
 *
 * Deliberately an effect in the root route rather than a React Router client
 * Instrumentation (the shape app/datadog-instrumentation.client.ts uses for RUM):
 * `navigate` instrumentation resolves when the navigation completes, which is BEFORE React
 * commits the new render — so `document.title` there is still the previous page's, and
 * walking off a results page would hand Google "Dermatologists in Portland, OR" attached
 * to the next page's URL. `useEffect` runs after the commit that `<Meta>` updates the title
 * in, so the title always matches the path being reported.
 *
 * The trade-off is that a navigation into the root ErrorBoundary is not counted — `App`
 * does not render then. Acceptable: those pages are errors, and they are already covered by
 * Datadog.
 *
 * Keyed on `pathname` only, not `search`. The results page keeps its filters in the query
 * string (`?insurance=`, see routes/results.tsx) and `page_location` drops the query
 * anyway, so keying on it would send repeat views of an identical URL.
 */
export const usePageView = (): void => {
  const { pathname } = useLocation()

  // Seeded with the landing path, which `initAnalytics` has already reported — so the
  // mount pass sends nothing and only real navigations do.
  const reportedPathname = useRef(pathname)

  useEffect(() => {
    const previous = reportedPathname.current
    if (previous === pathname) {
      return
    }

    reportedPathname.current = pathname
    // The path navigated away from becomes `page_referrer`: `document.referrer` never
    // updates on a client navigation, so without this every in-session page view would
    // report whatever external page opened the tab.
    trackPageView(previous)
  }, [pathname])
}
